import React, { useState, useEffect } from 'react';
import { roleService } from '../services/roleService';
import { userService } from '../services/userService';
import { Role, UserRegisterDto } from '../types/types';
import { UserPlus, CheckCircle, AlertCircle } from 'lucide-react';
import './UserRegister.css';

const UserRegister: React.FC = () => {
    const [formData, setFormData] = useState<UserRegisterDto>({
        username: '',
        password: '',
        role: 'Cashier',
    });
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    useEffect(() => {
        loadRoles();
    }, []);

    const loadRoles = async () => {
        try {
            const data = await roleService.getAllRoles();
            setRoles(data);
        } catch (error) {
            console.error('Error loading roles:', error);
            setMessage({ type: 'error', text: 'Error al cargar los roles' });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);

        try {
            await userService.createUser(formData);
            setMessage({ type: 'success', text: 'Usuario creado exitosamente' });
            setFormData({ username: '', password: '', role: 'Cashier' });
        } catch (error: any) {
            console.error('Error creating user:', error);
            setMessage({
                type: 'error',
                text: error.response?.data || 'Error al crear el usuario',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    return (
        <div className="user-register-container">
                <div className="page-header">
                    <div>
                        <h1>Registro de Usuarios</h1>
                        <p className="page-subtitle">Crear nuevos usuarios del sistema</p>
                    </div>
                    <UserPlus size={32} className="header-icon" />
                </div>

                <div className="register-card card">
                    <form onSubmit={handleSubmit} className="register-form">
                        <div className="form-group">
                            <label htmlFor="username">Nombre de Usuario</label>
                            <input
                                type="text"
                                id="username"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                required
                                placeholder="Ingrese el nombre de usuario"
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Contraseña</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                minLength={6}
                                placeholder="Mínimo 6 caracteres"
                                className="form-input"
                            />
                            <small className="form-hint">La contraseña debe tener al menos 6 caracteres</small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="role">Rol</label>
                            <select
                                id="role"
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                required
                                className="form-select"
                            >
                                {roles.map((role) => (
                                    <option key={role.id} value={role.name}>
                                        {role.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {message && (
                            <div className={`alert alert-${message.type}`}>
                                {message.type === 'success' ? (
                                    <CheckCircle size={20} />
                                ) : (
                                    <AlertCircle size={20} />
                                )}
                                <span>{message.text}</span>
                            </div>
                        )}

                        <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                            {loading ? 'Creando...' : 'Crear Usuario'}
                        </button>
                    </form>
                </div>
            </div>
    );
};

export default UserRegister;
